/**
 * Where a tenant's rent transfer should go (COL-533).
 *
 * Lifted out of `Dashboard.tsx`, where it was private to a 9,000-line V1
 * component. V2's payments card needs the same answer, and the one thing that
 * must not happen is two implementations of "which account does this tenant
 * pay into" drifting apart — one of them would then print a real IBAN that
 * takes real rent to the wrong bank account.
 *
 * V2 previously read `VITE_COLIVIQ_IBAN` instead, a THIRD source next to this
 * resolution and the `platform_bank_accounts` table. That variable is unset in
 * dev, so the card fell back to "Bank details on request" and tenants simply
 * lost the IBAN V1 had always shown them (Neel, 14 Oct: "why cant we show the
 * IBAN for rent to tenants anymore?"). The env var is gone from that path.
 *
 * Resolution order, per room, unchanged from V1:
 *   1. `room_payment_config` — an explicit per-room override set by an admin
 *   2. `get_room_rent_account` — the resolved rent-receipt account
 *   3. the CoLiviq account below — last resort, and ONLY for company_collects
 *
 * Step 3 never applies under owner_collects: falling back there would pay
 * CoLiviq for rent the owner is collecting. If the owner's account is not
 * configured we surface nothing valid and the caller shows its unavailable
 * state, which is the honest answer.
 */

/**
 * CoLiviq's own rent account, the company_collects last resort.
 *
 * Hardcoded, which is not ideal for a live bank account — but it is V1's
 * long-standing behaviour and this module exists to stop it being duplicated,
 * not to change where money goes. Replacing it with a resolved
 * `platform_bank_accounts` read is tracked separately; doing it here would
 * change the destination account in the same change that moves the lookup.
 */
export const COLIVIQ_DEFAULT_BANK_TRANSFER = {
  accountHolder: "COLIVIQ GMBH",
  iban: "DE55701500001009577022",
  bic: "SSKMDEMMXXX",
  bankName: "Stadtsparkasse München",
} as const;

export type RoomPaymentConfig = {
  bank_iban?: string | null;
  bank_account_holder?: string | null;
  bank_bic?: string | null;
  bank_name?: string | null;
  bank_transfer_enabled?: boolean | null;
} | null;

export type RoomRentAccount = {
  party?: string | null;
  collection_mode?: string | null;
  holder?: string | null;
  iban?: string | null;
  bic?: string | null;
  bank_name?: string | null;
} | null;

export type BankTransferDetails = {
  accountHolder: string;
  iban: string;
  bic: string;
  bankName: string;
  /** Who the money is going to ("Owner" / "CoLiviq"), for labelling. */
  party: string;
};

/**
 * The transfer destination for this room, or null when there is no valid one.
 *
 * Null is a real answer, not a loading state: an owner_collects room whose
 * owner bank account is not set up yet has nowhere legitimate to send rent,
 * and inventing a destination is the one failure mode this whole module
 * exists to prevent.
 */
export function resolveBankTransferDetails({
  paymentConfig,
  rentAccount,
  ownerCollects,
  bankTransferEnabled,
}: {
  paymentConfig: RoomPaymentConfig;
  rentAccount: RoomRentAccount;
  ownerCollects: boolean;
  bankTransferEnabled: boolean;
}): BankTransferDetails | null {
  if (!bankTransferEnabled) return null;

  const pick = (cfg: string, res: string, fallback: string) =>
    cfg || res || (ownerCollects ? "" : fallback);

  const iban = pick(
    (paymentConfig?.bank_iban ?? "").replace(/\s/g, ""),
    (rentAccount?.iban ?? "").replace(/\s/g, ""),
    COLIVIQ_DEFAULT_BANK_TRANSFER.iban
  );
  // No valid destination (e.g. owner_collects but owner bank not set up yet).
  if (!iban) return null;

  return {
    accountHolder: pick(
      (paymentConfig?.bank_account_holder ?? "").trim(),
      (rentAccount?.holder ?? "").trim(),
      COLIVIQ_DEFAULT_BANK_TRANSFER.accountHolder
    ),
    iban,
    bic: pick(
      (paymentConfig?.bank_bic ?? "").trim(),
      (rentAccount?.bic ?? "").trim(),
      COLIVIQ_DEFAULT_BANK_TRANSFER.bic
    ),
    bankName: pick(
      (paymentConfig?.bank_name ?? "").trim(),
      (rentAccount?.bank_name ?? "").trim(),
      COLIVIQ_DEFAULT_BANK_TRANSFER.bankName
    ),
    party: rentAccount?.party ?? "coliviq",
  };
}
